"use client";

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "next/navigation";
import { useSearch } from "./SearchProvider";
import RangeDateTimePicker from "./RangeDateTimePicker";
import { fetchDeliveryPlaces, type ApiCity, type DeliveryPlace } from "@/lib/api";
import { useLocale } from "@/lib/locale-context";
import { track } from "@/lib/metrika";

function scrollToCars() {
  document.getElementById("rent")?.scrollIntoView({ behavior: "smooth", block: "start" });
}



function ymd(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtDate(d: Date | null) {
  if (!d) return "";
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}
function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
const MIN_DAYS = 1; // минимальный срок аренды (потом — из настроек города)

// Лид-тайм: при старте «сегодня» минимальное время = сейчас + 2 ч, округлённое
// вверх до 15 минут (на подготовку ав), либо 23:45 если перевалило за полночь.
function leadFloorTime(): string {
  const d = new Date(Date.now() + 2 * 60 * 60 * 1000);
  const minutes = Math.ceil(d.getMinutes() / 15) * 15;
  d.setMinutes(minutes, 0, 0);
  if (d.getDate() !== new Date().getDate()) return "23:45";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// Время по умолчанию, когда выбрана только дата: 12:00, но если 12:00 недоступно
// (раньше минимально допустимого, напр. из-за лид-тайма) — первое доступное (minTime).
function defaultTime(minTime?: string): string {
  return minTime && "12:00" < minTime ? minTime : "12:00";
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden className={`size-5 shrink-0 text-text-muted transition-transform duration-200 ${open ? "rotate-180" : ""}`}>
      <path d="M6 9.5L12 15.5L18 9.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ============ Portal popover (выходит за overflow родителей) ============ */
function Popover({
  anchor, width, popRef, children,
}: {
  anchor: HTMLElement | null; width?: number; popRef: React.RefObject<HTMLDivElement | null>; children: ReactNode;
}) {
  const [pos, setPos] = useState<{ top: number; left: number; w: number } | null>(null);

  useLayoutEffect(() => {
    if (!anchor) return;
    const update = () => {
      const r = anchor.getBoundingClientRect();
      const w = width ?? r.width; // без явной ширины — по ширине поля-якоря
      const left = Math.max(12, Math.min(r.left, window.innerWidth - w - 12));
      setPos({ top: r.bottom + 8, left, w });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchor, width]);

  if (!anchor || !pos) return null;
  return createPortal(
    <div
      ref={popRef}
      style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.w, zIndex: 100 }}
      className="overflow-hidden rounded-2xl bg-surface shadow-[0_16px_40px_rgba(20,29,69,0.2)] ring-1 ring-border"
    >
      {children}
    </div>,
    document.body,
  );
}

/* ============ Поле (вид из Фигмы) ============ */
function Field({
  label, value, showChevron, open, anchorRef, onClick, className = "", btnClass = "",
}: {
  label: string; value: string; showChevron?: boolean; open: boolean;
  anchorRef: (el: HTMLButtonElement | null) => void;
  onClick: () => void; className?: string; btnClass?: string;
}) {
  return (
    <div className={`relative ${className}`}>
      <button
        ref={anchorRef}
        type="button"
        onClick={onClick}
        className={`flex h-[72px] w-full cursor-pointer items-center justify-between px-6 transition ${open ? "bg-white" : "bg-bg hover:bg-white"} ${btnClass}`}
      >
        <span className={`truncate text-[18px] leading-6 ${value ? "text-text" : "text-text-muted"}`}>
          {value || label}
        </span>
        {showChevron && <ChevronIcon open={open} />}
      </button>
    </div>
  );
}

type Pop = null | "city" | "pickup" | "dates";

function isoToDate(s: string): Date { return new Date(s + "T00:00:00"); }

export default function SearchBar() {
  const { t, locale } = useLocale();
  const { cities, activeCity, selectCity, runSearch, lockedCity, pickupGuid, setPickupGuid, setClass, setFilters } = useSearch();
  // Страница города: вместо выбора города — место подачи (по индексу, дефолт первый).
  const [places, setPlaces] = useState<DeliveryPlace[]>([]);
  useEffect(() => {
    if (!lockedCity || !activeCity) return;
    let off = false;
    fetchDeliveryPlaces(activeCity.station).then((d) => {
      if (off) return;
      setPlaces(d.cities);
      if (d.cities.length && !pickupGuid) setPickupGuid(d.cities[0].guid); // дефолт = первый по индексу
    });
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedCity, activeCity?.station]);
  const pickup = places.find((p) => p.guid === pickupGuid) || null;

  // Страница города, открытая попапом «Подобрать автомобиль» или КЛИКОМ ПО
  // БАННЕРУ с пресетами. Из URL применяем: фильтры (class/brand/model/delivery),
  // даты (start/end или start_offset+rental_days) — и при наличии дат сразу
  // запускаем поиск. Параметры — те же, что строит buildBannerLink.
  const searchParams = useSearchParams();
  const lastAppliedQS = useRef<string>("__init__");
  useEffect(() => {
    if (!lockedCity || !activeCity) return;
    const sp = new URLSearchParams(searchParams?.toString() || "");
    const qs = sp.toString();
    if (qs === lastAppliedQS.current) return; // тот же query — не переприменяем
    lastAppliedQS.current = qs;
    const s = sp.get("start"), e = sp.get("end");
    const startOffset = sp.get("start_offset");
    const rentalDays = sp.get("rental_days");
    const cls = sp.get("class");
    const brand = sp.get("brand");
    const modelsParam = sp.get("models");
    const delivery = sp.get("delivery");
    const hasAny = s || e || startOffset || rentalDays || cls || brand || modelsParam || delivery;
    if (!hasAny) return;

    // Место подачи из URL (иначе дефолтится первым — эффект выше).
    if (delivery) setPickupGuid(delivery);

    // Вычисляем даты: явные start/end приоритетнее, иначе из offset+срок.
    let sd = s || "", ed = e || "";
    if (!sd && (startOffset !== null || rentalDays)) {
      const base = new Date();
      const off = startOffset !== null ? Number(startOffset) || 0 : 0;
      base.setDate(base.getDate() + off);
      sd = ymd(base);
      const days = rentalDays ? Math.max(1, Number(rentalDays)) : 3;
      const endD = new Date(base); endD.setDate(endD.getDate() + days);
      ed = ymd(endD);
    }
    const st = sp.get("start_time") || "12:00";
    const et = sp.get("end_time") || "12:00";

    if (sd && ed) {
      setDateFrom(isoToDate(sd)); setDateTo(isoToDate(ed));
      setTimeFrom(st); setTimeTo(et);
      const days = Math.max(1, Math.round((isoToDate(ed).getTime() - isoToDate(sd).getTime()) / 86_400_000));
      // Сначала поиск (он сбрасывает фильтры), затем применяем пресет-фильтры.
      runSearch({ station: activeCity.station, startDate: sd, startTime: st, endDate: ed, endTime: et, days });
    }
    // Пресет-фильтры (класс/марка/подборка моделей) — поверх результата/парка.
    if (cls) setClass(cls);
    const modelNames = modelsParam ? modelsParam.split("|").map((p) => (p.split("__")[0] || "").trim()).filter(Boolean) : [];
    if (brand || modelNames.length) {
      setFilters({ brand: brand ? brand.toUpperCase() : null, model: null, fuel: null, drive: null, models: modelNames.length ? modelNames : null });
    }
    scrollToCars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedCity, activeCity?.station, searchParams]);
  // Город привязан к общему activeCity — двусторонняя синхронизация с каруселью
  // «Города»: клик в карусели подставляет город сюда, выбор тут подсвечивает
  // карточку города в карусели (обе читают/пишут activeCity).
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  // Мобайл: одно поле «Даты» + пикер в bottom-sheet (как на Солнышко).
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const [timeFrom, setTimeFrom] = useState("");
  const [timeTo, setTimeTo] = useState("");
  const [open, setOpen] = useState<Pop>(null);
  const city = (locale === "en" && (activeCity as { name_en?: string } | null)?.name_en ? (activeCity as { name_en?: string }).name_en : activeCity?.name) ?? "";

  // Город выбран → показываем автопарк города (статика). НЕ скроллим: скролл к
  // списку только по кнопке «Найти машину».
  const onPickCity = (c: ApiCity) => {
    setOpen(null);
    selectCity(c);
  };

  // «Найти машину» → поиск свободных машин по датам (нужны город и обе даты).
  const onFind = () => {
    if (!activeCity || !dateFrom || !dateTo) {
      setOpen(!activeCity ? "city" : "dates");
      return;
    }
    const days = Math.max(1, Math.round((dateTo.getTime() - dateFrom.getTime()) / 86_400_000));
    runSearch({
      station: activeCity.station,
      startDate: ymd(dateFrom),
      startTime: timeFrom || "12:00",
      endDate: ymd(dateTo),
      endTime: timeTo || "12:00",
      days,
    });
    track("search_submit", { city: activeCity.slug });
    scrollToCars();
  };

  const rootRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const cityAnchor = useRef<HTMLButtonElement | null>(null);
  const pickupAnchor = useRef<HTMLButtonElement | null>(null);
  const datesAnchor = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    // Модалка дат на мобиле закрывается своим бэкдропом/«Готово» — глобальный
    // обработчик клика-вне её бы закрывал при выборе даты (портал вне rootRef).
    if (isMobile && open === "dates") return;
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (popRef.current?.contains(t)) return;
      setOpen(null);
    }
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setOpen(null); }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open, isMobile]);

  // Модалка дат на мобиле — блокируем прокрутку страницы под ней.
  useEffect(() => {
    if (isMobile && open === "dates") {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [open, isMobile]);

  const toggle = (p: Pop) => setOpen((cur) => (cur === p ? null : p));

  return (
    <>
      <div
        ref={rootRef}
        className="mx-auto flex w-full max-w-[1200px] flex-col overflow-hidden rounded-2xl bg-bg shadow-[var(--shadow-card)] md:flex-row md:items-stretch"
      >
        {lockedCity ? (
          <Field label={t("search.place")} value={pickup ? (locale === "en" && pickup.name_en ? pickup.name_en : pickup.name) : ""} showChevron open={open === "pickup"} anchorRef={(el) => (pickupAnchor.current = el)} onClick={() => toggle("pickup")} className="flex-1 md:max-w-[320px]" />
        ) : (
          <Field label={t("search.city")} value={city} showChevron open={open === "city"} anchorRef={(el) => (cityAnchor.current = el)} onClick={() => toggle("city")} className="flex-1 md:max-w-[320px]" />
        )}
        <span className="hidden w-px bg-border md:block" />

        {/* Мобайл: ОДНО поле «Даты» (открывает bottom-sheet) */}
        <Field
          label={t("search.dates")}
          value={dateFrom && dateTo ? `${fmtDate(dateFrom)}, ${timeFrom} — ${fmtDate(dateTo)}, ${timeTo}` : ""}
          open={open === "dates"}
          anchorRef={() => {}}
          onClick={() => toggle("dates")}
          className="flex-1 md:hidden"
        />
        {/* Десктоп: прежние 4 поля */}
        <div className="hidden md:contents">
          <Field label={t("search.pickup")} value={fmtDate(dateFrom)} open={open === "dates"} anchorRef={(el) => (datesAnchor.current = el)} onClick={() => toggle("dates")} className="flex-1 md:max-w-[190px]" />
          <span className="hidden my-6 w-px bg-border md:block" />
          <Field label={t("search.time")} value={timeFrom} open={open === "dates"} anchorRef={() => {}} onClick={() => toggle("dates")} className="flex-1 md:max-w-[150px]" />
          <span className="hidden w-px bg-border md:block" />
          <Field label={t("search.return")} value={fmtDate(dateTo)} open={open === "dates"} anchorRef={() => {}} onClick={() => toggle("dates")} className="flex-1 md:max-w-[190px]" />
          <span className="hidden my-6 w-px bg-border md:block" />
          <Field label={t("search.time")} value={timeTo} open={open === "dates"} anchorRef={() => {}} onClick={() => toggle("dates")} className="flex-1 md:max-w-[150px]" />
        </div>

        <button
          type="button"
          onClick={onFind}
          className="flex h-[72px] shrink-0 cursor-pointer items-center justify-center bg-danger px-6 text-[18px] leading-6 text-white transition hover:bg-[#c81f17] md:w-[198px]"
        >
          {t("search.find")}
        </button>
      </div>

      {open === "city" && (
        <Popover anchor={cityAnchor.current} popRef={popRef}>
          <ul className="max-h-[300px] overflow-y-auto py-1">
            {cities.length === 0 ? (
              <li className="px-6 py-2.5 text-[16px] text-text-muted">{t("search.loadingCities")}</li>
            ) : (
              cities.map((c) => (
                <li key={c.station}>
                  <button
                    type="button"
                    onClick={() => onPickCity(c)}
                    className={`block w-full cursor-pointer px-6 py-2.5 text-left text-[16px] transition hover:bg-bg ${c.station === activeCity?.station ? "font-medium text-primary" : "text-text"}`}
                  >
                    {c.name}
                  </button>
                </li>
              ))
            )}
          </ul>
        </Popover>
      )}

      {open === "pickup" && (
        <Popover anchor={pickupAnchor.current} popRef={popRef}>
          <ul className="max-h-[300px] overflow-y-auto py-1">
            {places.length === 0 ? (
              <li className="px-6 py-2.5 text-[16px] text-text-muted">{t("search.loadingPlaces")}</li>
            ) : (
              places.map((p) => (
                <li key={p.guid}>
                  <button
                    type="button"
                    onClick={() => { track("pickup_select", { city: activeCity?.slug }); setPickupGuid(p.guid); setOpen(null); }}
                    className={`flex w-full cursor-pointer items-start justify-between gap-3 px-6 py-2.5 text-left text-[15px] transition hover:bg-bg ${p.guid === pickupGuid ? "font-medium text-primary" : "text-text"}`}
                  >
                    <span className="break-words leading-snug">{locale === "en" && p.name_en ? p.name_en : p.name}</span>
                    <span className="shrink-0 whitespace-nowrap text-[14px] font-semibold text-primary">{(p.price || 0) > 0 ? `+${(p.price || 0).toLocaleString("ru-RU")} ₽` : t("search.free")}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </Popover>
      )}

      {open === "dates" && (() => {
        const picker = (
          <RangeDateTimePicker
            startISO={dateFrom ? ymd(dateFrom) : ""}
            endISO={dateTo ? ymd(dateTo) : ""}
            startTime={timeFrom}
            endTime={timeTo}
            minDate={ymd(new Date())}
            minTime={leadFloorTime()}
            minDays={MIN_DAYS}
            onRangeChange={(s, e) => {
              setDateFrom(isoToDate(s));
              setDateTo(e ? isoToDate(e) : null);
            }}
            onStartTime={setTimeFrom}
            onEndTime={setTimeTo}
            onClose={() => setOpen(null)}
          />
        );
        return isMobile ? (
          // Центрированная модалка (как на Солнышко): период + время → «Готово».
          createPortal(
            <div
              onClick={() => setOpen(null)}
              style={{ position: "fixed", inset: 0, zIndex: 9000, background: "rgba(13,20,42,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ background: "#fff", width: "100%", maxWidth: 440, maxHeight: "92vh", overflowY: "auto", borderRadius: 20, boxShadow: "0 20px 60px rgba(13,20,42,0.3)" }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 18px 4px" }}>
                  <span style={{ fontSize: 20, fontWeight: 700, color: "#141D45" }}>{t("search.dates")}</span>
                  <button onClick={() => setOpen(null)} aria-label={t("common.close")} style={{ width: 32, height: 32, borderRadius: "50%", border: "none", background: "#F5F5F7", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                  </button>
                </div>
                {picker}
              </div>
            </div>,
            document.body,
          )
        ) : (
          <Popover anchor={datesAnchor.current} width={600} popRef={popRef}>{picker}</Popover>
        );
      })()}
    </>
  );
}
